// ManagerDetailScreen — one manager (docs/miniapp-m3-tz.md §3.6).
// Header card (avatar + live load) + two live tabs: Задачи (open tasks) and
// Активность (derived completed/assigned feed). The Сделки drill stays via the
// shared /deals list; full per-manager deals tab is a later refinement.
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { fetchTeamMember } from '../../api/director'
import type { TeamActivityItem, TeamOpenTask } from '../../api/types'
import { Avatar, Button, EmptyState, Icon, ScreenSkeleton, t } from '../../design'
import { fmtDateTime } from '../../time'

type Tab = 'tasks' | 'activity'

function relTime(iso: string): string {
  return fmtDateTime(iso)
}

function TaskRow({ task, onClick }: { task: TeamOpenTask; onClick: () => void }) {
  const overdue = task.status === 'overdue'
  return (
    <div className={`task task-${overdue ? 'overdue' : 'pending'}`} onClick={onClick}>
      <span className="task-icon"><Icon.ListTodo size={18} sw={2} /></span>
      <div className="task-body">
        <div className="t-body-s ellipsis">{task.title}</div>
        <div className="t-caption ellipsis">#{task.deal_id}{overdue ? ` · ${t('st.overdue')}` : ''}</div>
      </div>
    </div>
  )
}

function ActivityRow({ item }: { item: TeamActivityItem }) {
  const completed = item.kind === 'completed'
  return (
    <div className="row middle gap-3" style={{ padding: '8px 0' }}>
      <span style={{ width: 28, height: 28, borderRadius: 8, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: completed ? 'var(--success-bg)' : 'var(--surface-2)', color: completed ? 'var(--success)' : 'var(--text-2)' }}>
        {completed ? <Icon.Check size={14} sw={2.5} /> : <Icon.Plus size={14} sw={2.5} />}
      </span>
      <div className="flex-1" style={{ minWidth: 0 }}>
        <div className="t-caption-s ellipsis" style={{ color: 'var(--text-1)' }}>
          {completed ? t('act.completed') : t('act.assigned')} · {item.title}
        </div>
        <div className="t-caption">{relTime(item.at)}</div>
      </div>
    </div>
  )
}

export function ManagerDetailScreen() {
  const { manager_id } = useParams<{ manager_id: string }>()
  const id = Number(manager_id)
  const navigate = useNavigate()
  const [tab, setTab] = useState<Tab>('tasks')

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['team', id],
    queryFn: () => fetchTeamMember(id),
    enabled: Number.isFinite(id) && id > 0,
  })

  if (isLoading) return <ScreenSkeleton kind="today" />
  if (isError || !data) {
    return (
      <div className="scroll-y" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', gap: 14, padding: 24 }}>
        <span style={{ width: 72, height: 72, borderRadius: 18, background: 'var(--surface-2)', color: 'var(--text-3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon.CloudOff size={30} sw={2} />
        </span>
        <div className="t-h2">{t('task.loadError')}</div>
        <div className="row gap-2">
          <Button variant="secondary" icon={<Icon.Refresh size={16} sw={2} />} onClick={() => refetch()}>{t('btn.retry')}</Button>
          <Button variant="ghost" icon={<Icon.ArrowLeft size={16} sw={2} />} onClick={() => navigate('/team')}>{t('nav.team')}</Button>
        </div>
      </div>
    )
  }

  const m = data.manager
  const name = m.name || m.short_name

  return (
    <div className="scroll-y" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '12px 16px 0' }}>
        {/* Header card */}
        <div className="card card-16">
          <div className="row middle gap-3" style={{ marginBottom: 10 }}>
            <Avatar name={name} size={48} />
            <div className="flex-1" style={{ minWidth: 0 }}>
              <div className="t-h1 ellipsis">{name}</div>
              <div className="t-caption">{m.status === 'active' ? t('team.active') : t('team.offline')} · #{m.id}</div>
            </div>
          </div>
          <div className="row gap-2">
            <div className="card flex-1" style={{ padding: 10, textAlign: 'center', background: 'var(--surface-2)', boxShadow: 'none' }}>
              <div className="t-h1" style={{ fontVariantNumeric: 'tabular-nums' }}>{m.stats.open_tasks}</div>
              <div className="t-label">{t('team.open')}</div>
            </div>
            <div className="card flex-1" style={{ padding: 10, textAlign: 'center', background: 'var(--surface-2)', boxShadow: 'none' }}>
              <div className="t-h1" style={{ color: m.stats.overdue ? 'var(--danger)' : undefined, fontVariantNumeric: 'tabular-nums' }}>{m.stats.overdue}</div>
              <div className="t-label">{t('team.overdue')}</div>
            </div>
            <div className="card flex-1" style={{ padding: 10, textAlign: 'center', background: 'var(--surface-2)', boxShadow: 'none' }}>
              <div className="t-h1" style={{ fontVariantNumeric: 'tabular-nums' }}>{m.stats.avg_score != null ? m.stats.avg_score.toFixed(1) : '—'}</div>
              <div className="t-label">{t('dir.avgScore')}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ position: 'sticky', top: 0, zIndex: 4, marginTop: 12 }}>
        <div className="tabs">
          <button className={`tab ${tab === 'tasks' ? 'is-active' : ''}`} onClick={() => setTab('tasks')}>{t('mgr.tabTasks')}</button>
          <button className={`tab ${tab === 'activity' ? 'is-active' : ''}`} onClick={() => setTab('activity')}>{t('mgr.tabActivity')}</button>
        </div>
      </div>

      <div style={{ padding: '12px 16px 24px', flex: 1 }}>
        {tab === 'tasks' ? (
          data.open_tasks.length === 0 ? (
            <EmptyState icon={<Icon.Check size={26} sw={2.25} />} title={t('mgr.noTasks')} />
          ) : (
            <div className="col gap-2">
              {data.open_tasks.map((task) => (
                <TaskRow key={task.id} task={task} onClick={() => navigate(`/task/${task.id}`)} />
              ))}
            </div>
          )
        ) : data.activity.length === 0 ? (
          <EmptyState icon={<Icon.Activity size={26} />} title={t('mgr.noActivity')} />
        ) : (
          <div className="card card-16">
            {data.activity.map((a, i) => (
              <div key={`${a.task_id}-${i}`}>
                {i > 0 && <div className="div" />}
                <ActivityRow item={a} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
